import React, { Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";

// Protected Route
import ProtectedRoute from "./ProtectedRoute";

// Utilities
import { loadableWithChunkRecovery } from "../utils/loadableWithChunkRecovery";
import { logToMyService } from "../utils/errorLogger";

// Components
import LoadingSpinner from "../components/CustomComponents/LoadingSpinner";
import NotFound from "../screens/NotFound";
import TestError from "../customComponents/TestError";
import ErrorBoundary from "../customComponents/ErrorBoundary";
import About from "../screens/about";
import PendingGuestList from "../screens/eventDetails/hostEventDetails/pendingGuestList";
import PendingConnectionRequestsList from "../screens/Analytics/connectionRequests/pending-connection-requests-list";
import MatchedConnectionRequestsList from "../screens/Analytics/connectionRequests/matched-connection-requests-list";
import PendingApprovalList from "../screens/eventDetails/hostEventDetails/pendingApprovalList";

// Layouts
const HomeLayout = loadableWithChunkRecovery(
  () => import("../screens/homeLayout")
);
const AuthScreenLayout = loadableWithChunkRecovery(
  () => import("../screens/auth/layout/AuthScreenLayout")
);
const HelpLayout = loadableWithChunkRecovery(
  () => import("../screens/help/helpLayout")
);
const CreateEventLayout = loadableWithChunkRecovery(
  () => import("../screens/hostCenter/layout/createEventLayout")
);

// Auth Screens
const Login = loadableWithChunkRecovery(() => import("../screens/auth/login"));
const OtpScreen = loadableWithChunkRecovery(
  () => import("../screens/auth/otp")
);
const SignUp = loadableWithChunkRecovery(
  () => import("../screens/auth/signUp")
);
const Registration = loadableWithChunkRecovery(
  () => import("../screens/auth/registration")
);
const ProfilePicUpload = loadableWithChunkRecovery(
  () => import("../screens/auth/profile-pic-upload")
);

// Help Screens
const TermsOfService = loadableWithChunkRecovery(
  () => import("../screens/help/terms-of-service")
);
const PrivacyPolicy = loadableWithChunkRecovery(
  () => import("../screens/help/privacy-policy")
);
const Eula = loadableWithChunkRecovery(() => import("../screens/help/eula"));
const HostAgreement = loadableWithChunkRecovery(
  () => import("../screens/help/host-agreement")
);
const SafetyNotice = loadableWithChunkRecovery(
  () => import("../screens/help/safety-notice")
);
const FAQPage = loadableWithChunkRecovery(() => import("../screens/help/faq"));

// Main Screens
const Discover = loadableWithChunkRecovery(() => import("../screens/discover"));
const LandingPage = loadableWithChunkRecovery(
  () => import("../screens/landingPage")
);
const Events = loadableWithChunkRecovery(() => import("../screens/events"));
const HostCenter = loadableWithChunkRecovery(
  () => import("../screens/hostCenter")
);
const EventDetails = loadableWithChunkRecovery(
  () => import("../screens/eventDetails")
);
const Profile = loadableWithChunkRecovery(() => import("../screens/profile"));

// Host Center Screens
const BasicDetails = loadableWithChunkRecovery(
  () => import("../screens/hostCenter/basicDetails")
);
const EventInfo = loadableWithChunkRecovery(
  () => import("../screens/hostCenter/eventInfo")
);
const TicketsAndPromo = loadableWithChunkRecovery(
  () => import("../screens/hostCenter/tickets&promo")
);
const GeneralSettings = loadableWithChunkRecovery(
  () => import("../screens/hostCenter/generalSettings")
);
const Payouts = loadableWithChunkRecovery(
  () => import("../screens/hostCenter/payouts")
);
const CreateEvent = loadableWithChunkRecovery(
  () => import("../screens/hostCenter/create-event")
);
const HostEventDetails = loadableWithChunkRecovery(
  () => import("../screens/eventDetails/hostEventDetails")
);
const SubscriptionHostCenterDashboard = loadableWithChunkRecovery(
  () => import("../screens/hostCenter/subscriptions")
);

// Analytics Screens
const Analytics = loadableWithChunkRecovery(
  () => import("../screens/Analytics")
);
const Revenue = loadableWithChunkRecovery(
  () => import("../screens/Analytics/revenue")
);
const Guests = loadableWithChunkRecovery(
  () => import("../screens/Analytics/guests")
);
const Matches = loadableWithChunkRecovery(
  () => import("../screens/Analytics/matches")
);
const ConnectionRequests = loadableWithChunkRecovery(
  () => import("../screens/Analytics/connectionRequests")
);

// Text / Email Blast Screens
const TextBlastDetails = loadableWithChunkRecovery(
  () => import("../screens/textBlast/textBlastDetails")
);
const RecipientSelection = loadableWithChunkRecovery(
  () => import("../screens/recipientSelection")
);
const TextBlast = loadableWithChunkRecovery(
  () => import("../screens/text-blast")
);
const RefundRequest = loadableWithChunkRecovery(
  () => import("../screens/refundRequest")
);

const EventCancelled = loadableWithChunkRecovery(
  () => import("../screens/eventDetails/eventCancelled")
);

const Shared = loadableWithChunkRecovery(() => import("../screens/shared"));
const PayoutAccounts = loadableWithChunkRecovery(
  () => import("../screens/payoutAccounts")
);
// Loader
const Loading = () => <LoadingSpinner loading={true} />;

const routes = createBrowserRouter([
  {
    path: "",
    element: (
      <ErrorBoundary onError={logToMyService}>
        <Suspense fallback={<Loading />}>
          <HomeLayout />
        </Suspense>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<Loading />}>
            <LandingPage />
          </Suspense>
        ),
      },
      {
        path: "discover",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <Discover />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "payout-accounts",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <PayoutAccounts />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "shared",
        element: (
          <Suspense fallback={<Loading />}>
            <Shared />
          </Suspense>
        ),
      },
      {
        path: "events",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <Events />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "host-center",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <HostCenter />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "refund-requests",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <RefundRequest />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "host-center/event-details",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <HostEventDetails />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "host-center/subscribers",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <SubscriptionHostCenterDashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "host-center/analytics",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <Analytics />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "host-center/analytics/revenue",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <Revenue />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "host-center/analytics/guests",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <Guests />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "host-center/analytics/matches",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <Matches />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "host-center/analytics/connection-request",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <ConnectionRequests />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      {
        path: "host-center/analytics/pending-connection-request-list",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <PendingConnectionRequestsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      {
        path: "host-center/analytics/matched-connection-request-list",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <MatchedConnectionRequestsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "event-details",
        element: (
          <Suspense fallback={<Loading />}>
            <EventDetails />
          </Suspense>
        ),
      },
      {
        path: "create-events",
        element: (
          <Suspense fallback={<Loading />}>
            <CreateEvent />
          </Suspense>
        ),
      },
      {
        path: "create-event",
        element: (
          <Suspense fallback={<Loading />}>
            <CreateEventLayout />
          </Suspense>
        ),
        children: [
          {
            path: "basic-details",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<Loading />}>
                  <BasicDetails />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "event-info",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<Loading />}>
                  <EventInfo />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "tickets&promo",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<Loading />}>
                  <TicketsAndPromo />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "general-settings",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<Loading />}>
                  <GeneralSettings />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "payouts",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<Loading />}>
                  <Payouts />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "text-blast",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <TextBlast />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "recipient-selection",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <RecipientSelection />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "textBlast-details",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <TextBlastDetails />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "profile",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <Profile />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      {
        path: "host-center/pending/guests",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <PendingGuestList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
            {
        path: "host-center/get-pending-payment-holds",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<Loading />}>
              <PendingApprovalList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },

  {
    path: "/help",
    element: (
      <Suspense fallback={<Loading />}>
        <HelpLayout />
      </Suspense>
    ),
    children: [
      {
        path: "terms-of-service",
        element: (
          <Suspense fallback={<Loading />}>
            <TermsOfService />
          </Suspense>
        ),
      },
      {
        path: "privacy-policy",
        element: (
          <Suspense fallback={<Loading />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
      {
        path: "eula",
        element: (
          <Suspense fallback={<Loading />}>
            <Eula />
          </Suspense>
        ),
      },
      {
        path: "host-agreement",
        element: (
          <Suspense fallback={<Loading />}>
            <HostAgreement />
          </Suspense>
        ),
      },
      {
        path: "safety-notice",
        element: (
          <Suspense fallback={<Loading />}>
            <SafetyNotice />
          </Suspense>
        ),
      },
      {
        path: "faq",
        element: (
          <Suspense fallback={<Loading />}>
            <FAQPage />
          </Suspense>
        ),
      },
    ],
  },

  {
    path: "about",
    element: (
      <Suspense fallback={<Loading />}>
        <About />
      </Suspense>
    ),
  },

  {
    path: "/auth",
    element: (
      <Suspense fallback={<Loading />}>
        <AuthScreenLayout />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<Loading />}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: "otp",
        element: (
          <Suspense fallback={<Loading />}>
            <OtpScreen />
          </Suspense>
        ),
      },
      {
        path: "signup",
        element: (
          <Suspense fallback={<Loading />}>
            <SignUp />
          </Suspense>
        ),
      },
      {
        path: "profile-pic-upload",
        element: (
          <Suspense fallback={<Loading />}>
            <ProfilePicUpload />
          </Suspense>
        ),
      },
      {
        path: "registration",
        element: (
          <Suspense fallback={<Loading />}>
            <Registration />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "/event-cancelled",
    element: (
      <ErrorBoundary onError={logToMyService}>
        <Suspense fallback={<Loading />}>
          <EventCancelled />
        </Suspense>
      </ErrorBoundary>
    ),
  },

  { path: "*", element: <NotFound /> },
  {
    path: "test-error",
    element: (
      <ErrorBoundary onError={logToMyService}>
        <TestError />
      </ErrorBoundary>
    ),
  },
]);

export default routes;
